/**
 * Dispatch, with the rejection path wired to a toast — or, for the cap, to the
 * upgrade dialog.
 *
 * The reducer is total: it returns a typed {@link CommandError} and leaves the
 * state untouched rather than throwing. That is only useful if something tells
 * the user, so every UI write goes through this hook rather than touching the
 * store directly.
 *
 * ## Two refusals, two surfaces
 *
 * An ordinary refusal — a stale screen, a payload the reducer will not have — is
 * a toast: "that change was not applied", with the field detail.
 *
 * A **`plan-limit`** refusal is not that. It only reaches this hook on a guest
 * workspace, where the store's own guard raised it before anything was applied
 * (`lib/plan-cap.ts`), and it means the thing the brewer asked for is not going
 * to exist until they delete something or upgrade. So it raises the upgrade
 * dialog and *no* toast: the dialog names the cap, says what lifts it, and a
 * toast beside it would be the same sentence twice. The server path in
 * `sync/sync-context.tsx` does show both, for the reason stated there — a
 * recipe the brewer was looking at disappears off the screen, which needs
 * announcing wherever they happen to be looking. Here nothing ever appeared.
 */

import type { CommandError, CommandOf, CommandType } from '@beerwright/commands';
import { useCallback, useMemo } from 'react';
import { useSession } from '@/auth/session-context';
import type { PlanLimitPrompt } from '@/components/plan-limit-dialog';
import { usePlanLimit } from '@/components/plan-limit-dialog';
import { useToast } from '@/components/toast';
import { BLEND_LIMIT, JOURNAL_LIMIT } from '@/lib/plan';
import { useWorkspaceStore } from './workspace-context';
import type { DispatchStep } from './workspace-store';

/** One line describing why a command bounced, with the field detail appended. */
export function describeCommandError(error: CommandError): string {
  if (error.issues.length === 0) return error.message;
  const detail = error.issues
    .slice(0, 3)
    .map((issue) => (issue.path === '' ? issue.message : `${issue.path}: ${issue.message}`))
    .join('; ');
  return `${error.message} ${detail}`;
}

/** What {@link useDispatch} returns. */
export interface DispatchApi {
  /** Apply a command; returns true when it landed. Rejections toast. */
  readonly dispatch: <K extends CommandType>(type: K, payload: CommandOf<K>['payload']) => boolean;
  /**
   * Apply several commands as **one** undo step. Rejections toast and apply
   * nothing. Used when one thought is several writes — a malt bill typed as
   * percentages becomes one `amountKg` per row.
   */
  readonly dispatchAll: (steps: readonly DispatchStep[], summary?: string) => boolean;
  readonly undo: () => void;
  readonly redo: () => void;
}

export function useDispatch(): DispatchApi {
  const store = useWorkspaceStore();
  const { toast } = useToast();
  const { show: showPlanLimit } = usePlanLimit();
  // Only the recipe cap is per-account — support can raise it — so it is read
  // off the entitlement while blends and journals come from the tier table.
  // The same three numbers `sync/sync-context.tsx` quotes for a server refusal.
  const recipeLimit = useSession().entitlement.recipeLimit;

  /** Announce a refusal: the dialog for the cap, a toast for everything else. */
  const announce = useCallback(
    (error: CommandError): void => {
      const prompt = planLimitPromptFor(error, recipeLimit);
      if (prompt !== null) {
        showPlanLimit(prompt);
        return;
      }
      toast({
        variant: 'error',
        title: 'That change was not applied',
        description: describeCommandError(error),
      });
    },
    [recipeLimit, showPlanLimit, toast],
  );

  const dispatch = useCallback(
    <K extends CommandType>(type: K, payload: CommandOf<K>['payload']): boolean => {
      const result = store.dispatch(type, payload);
      if (!result.ok) {
        announce(result.error);
        return false;
      }
      return true;
    },
    [store, announce],
  );

  const dispatchAll = useCallback(
    (steps: readonly DispatchStep[], summary?: string): boolean => {
      const result = store.dispatchAll(steps, summary);
      if (!result.ok) {
        announce(result.error);
        return false;
      }
      return true;
    },
    [store, announce],
  );

  const undo = useCallback(() => {
    const result = store.undo();
    if (result !== null && !result.ok) {
      toast({ variant: 'error', title: 'Undo failed', description: result.error.message });
    }
  }, [store, toast]);

  const redo = useCallback(() => {
    const result = store.redo();
    if (result !== null && !result.ok) {
      toast({ variant: 'error', title: 'Redo failed', description: result.error.message });
    }
  }, [store, toast]);

  return useMemo(
    () => ({ dispatch, dispatchAll, undo, redo }),
    [dispatch, dispatchAll, undo, redo],
  );
}

/**
 * The upgrade prompt a refusal deserves, or `null` when it is not a cap.
 *
 * The mapping is `sync/sync-context.tsx`'s, command for command and number for
 * number, so the dialog a guest meets is the dialog an account meets. `refused`
 * is always one: a batch stops at its first refusal, and an import dispatches
 * one recipe at a time, so exactly one document failed to appear.
 */
function planLimitPromptFor(error: CommandError, recipeLimit: number): PlanLimitPrompt | null {
  if (error.code !== 'plan-limit') return null;
  const type = error.commandType;
  if (type === 'blend.create' || type === 'blend.restore') {
    return { reason: 'blend-cap', limit: BLEND_LIMIT, refused: 1 };
  }
  if (type === 'journal.create' || type === 'journal.restore') {
    return { reason: 'journal-cap', limit: JOURNAL_LIMIT, refused: 1 };
  }
  return { reason: 'recipe-cap', limit: recipeLimit, refused: 1 };
}
